import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Sidebar from '../components/sidebar/Sidebar';
import NavbarAdmin from '../components/navbar/NavbarAdmin';
import { getAllProducts, createProduct, updateProduct, deleteProduct } from '../services/productService';
import { getAllCategories } from '../services/categoryService';
import { getAllSubcategories, getSubcategoriesByCategory, getCategoryOfSubcategory } from '../services/subcategoryService';
import swal from 'sweetalert';

const ProductsScreen = () => {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [filteredSubs, setFilteredSubs] = useState([]);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [dragActive, setDragActive] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', image: null, initialPrice: '', quantity: '', tva: 19, category: '', subcategory: '', reference: '', promoPercentage: 0, });
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [multipleCategoriesOfSub, setMultipleCategoriesOfSub] = useState([]);
  const [manuallySelectedCategory, setManuallySelectedCategory] = useState('');

  const fetchData = async (showRefreshLoader = false) => {
    try {
      if (showRefreshLoader) setIsRefreshing(true);
      const [p, c, s] = await Promise.all([getAllProducts(), getAllCategories(), getAllSubcategories()]);
      setProducts(p.data);
      setCategories(c.data);
      setSubcategories(s.data);
    } catch {
      swal('Erreur', 'Chargement échoué', 'error');
    } finally {
      if (showRefreshLoader) {
        setTimeout(() => setIsRefreshing(false), 1000);
      }
    }
  };


const handleInputChange = async (e) => {
  const { name, value, files } = e.target;

  if (name === 'category') {
    try {
      const res = await getSubcategoriesByCategory(value);
      setFilteredSubs(res.data);
      const isSubValid = res.data.some((sub) => sub._id === form.subcategory);

      setManuallySelectedCategory(value);
      setForm((f) => ({
        ...f,
        category: value,
        subcategory: isSubValid ? f.subcategory : '',
      }));
    } catch {
      swal('Erreur', 'Chargement sous-catégories', 'error');
    }
  } else if (name === 'subcategory') {
    if (!value) {
      setForm((f) => ({ ...f, subcategory: '', category: f.category }));
      setMultipleCategoriesOfSub([]);
      setFilteredSubs([]);
      return;
    }

    try {
      const res = await getCategoryOfSubcategory(value);
      const categoryIds = Array.isArray(res.data) ? res.data : [];

      if (categoryIds.length === 1) {
        const subs = await getSubcategoriesByCategory(categoryIds[0]);
        setFilteredSubs(subs.data);
        setForm((f) => ({
          ...f,
          subcategory: value,
          category: categoryIds[0],
        }));
        setMultipleCategoriesOfSub([]);
        setManuallySelectedCategory(categoryIds[0]);
      } else if (categoryIds.length > 1) {
        const resolvedCategory = manuallySelectedCategory || '';
        let subs = [];

        if (resolvedCategory) {
          const subRes = await getSubcategoriesByCategory(resolvedCategory);
          subs = subRes.data;
        }

        setMultipleCategoriesOfSub(categoryIds);
        setFilteredSubs(subs);
        setForm((f) => ({
          ...f,
          subcategory: value,
          category: resolvedCategory,
        }));
      }
    } catch {
      setForm((f) => ({ ...f, subcategory: value }));
    }
  } else {
    setForm({ ...form, [name]: files ? files[0] : value });
  }
};




  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setForm({ ...form, image: e.dataTransfer.files[0] });
    }
  };

  const handleSubmit = async () => {
    const { name, description, image, initialPrice, quantity, tva, category } = form;

    if (!name || !description || !initialPrice || !quantity || !tva || !category) {
      return swal('Erreur', 'Tous les champs sauf la sous-catégorie sont obligatoires.', 'warning');
    }

    if (!editMode && !image) {
      return swal('Erreur', 'L’image est requise.', 'warning');
    }

    setLoading(true);

    const completeSubmission = async () => {
      try {
        if (editMode) {
          await updateProduct(editingId, form);
        } else {
          await createProduct(form);
        }
        swal(editMode ? 'Modifié' : 'Ajouté', '', 'success');
        fetchData();
        resetForm();
        setShowModal(false);
        setEditMode(false);
        setEditingId(null);
      } catch {
        swal('Erreur', 'Erreur enregistrement', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (image) {
      const img = new Image();
      img.src = URL.createObjectURL(image);
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        if (aspectRatio < 0.8 || aspectRatio > 1.2) {
          setLoading(false);
          return swal('Erreur', 'L’image doit être carrée.', 'warning');
        }
        completeSubmission();
      };
    } else {
      completeSubmission();
    }
  };




  const handleDelete = async (id) => {
    const confirm = await swal({ title: 'Supprimer ?', icon: 'warning', buttons: true, dangerMode: true });
    if (confirm) {
      try {
        await deleteProduct(id); fetchData(); swal('Supprimé', '', 'success');
      } catch {
        swal('Erreur', 'Erreur suppression', 'error');
      }
    }
  };

  const handleEdit = async (product) => {
    const subId = typeof product.subcategory === 'string' ? product.subcategory : product.subcategory?._id || '';
    let catId = typeof product.category === 'string' ? product.category : product.category?._id || '';
    if (subId && !catId) {
      try {
        const res = await getCategoryOfSubcategory(subId);
        const categoryIds = Array.isArray(res.data) ? res.data : [];
        catId = categoryIds[0] || '';
      } catch {
        catId = '';
      }
    }try {
      const res = await getCategoryOfSubcategory(subId);
      const categoryIds = Array.isArray(res.data) ? res.data : [];
      if (categoryIds.length > 1) {
        setMultipleCategoriesOfSub(categoryIds);
      } else {
        setMultipleCategoriesOfSub([]);
      }
    } catch {
      setMultipleCategoriesOfSub([]);
    }
    try {
      const subRes = await getSubcategoriesByCategory(catId);
      const relatedSubs = subRes.data;
      const currentSub = relatedSubs.find((s) => s._id === subId);
      if (!currentSub) {
        const sub = subcategories.find((s) => s._id === subId);
        if (sub) relatedSubs.push(sub);
      }
      setFilteredSubs(relatedSubs);
    } catch {
      setFilteredSubs([]);
    }
    setForm({ name: product.name, description: product.description, image: null, initialPrice: product.initialPrice, quantity: product.quantity, tva: product.tva, category: catId, subcategory: subId, reference: product.reference || '' });
    setEditMode(true); setEditingId(product._id); setShowModal(true);
  };

  const resetForm = () => {
    setForm({ name: '', description: '', image: null, initialPrice: '', quantity: '', tva: 19, category: '', subcategory: '', reference: '' });
    setFilteredSubs([]);
  };

  useEffect(() => { fetchData(); }, []);

  const filtered = products.filter((p) => {
    const categoryName = categories.find((c) => c._id === (typeof p.category === 'string' ? p.category : p.category?._id))?.name || '';
    const subcategoryName = subcategories.find((s) => s._id === (typeof p.subcategory === 'string' ? p.subcategory : p.subcategory?._id))?.name || '';
    const createdByEmail = typeof p.addedBy === 'object' ? p.addedBy?.email : p.addedBy || '';
    const modifiedByEmail = typeof p.modifiedBy === 'object' ? p.modifiedBy?.email : p.modifiedBy || '';
    const valuesToSearch = [
      p.name,
      p.description,
      p.initialPrice?.toString(),
      p.quantity?.toString(),
      p.tva?.toString(),
      p.reference?.toString(),
      categoryName,
      subcategoryName,
      createdByEmail,
      modifiedByEmail
    ];
    return valuesToSearch.some(val => val?.toLowerCase().includes(search.toLowerCase()));
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="flex min-h-screen">
      {isRefreshing && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-80 text-center">
            <img src="/assets/loading.gif" alt="Chargement" className="mx-auto w-24 h-24 mb-4" />
            <h2 className="text-lg font-bold mb-2">Rafraîchissement en cours...</h2>
            <p className="text-gray-700">Merci de patienter un instant</p>
          </div>
        </div>
      )}

      <div className="md:hidden fixed top-0 left-0 right-0 z-50">
        <NavbarAdmin slug={slug} />
      </div>
      <div className="hidden md:flex">
        <Sidebar slug={slug} />
      </div>
      <main className="flex-1 md:ml-64 p-6 mt-16 md:mt-0">
        <div className="container mx-auto">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-paragaia">Produits</h1>
            <div className="flex gap-2">
            <button onClick={() => fetchData(true)} className="bg-pink-200 px-4 py-2 rounded">
              Rafraîchir
            </button>
              <button onClick={() => { setShowModal(true); setEditMode(false); resetForm(); }} className="bg-paragaia text-white px-4 py-2 rounded">Ajouter</button>
            </div>
          </div>
          <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
            <input type="text" placeholder="Rechercher..." value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }} className="w-full md:w-1/2 p-3 border rounded" />
            <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(+e.target.value); setCurrentPage(1); }} className="p-2 border rounded">
              {[12, 24, 48, 96].map((n) => <option key={n} value={n}>Afficher {n}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginated.map((p) => (
              <div key={p._id} className="rounded-2xl border shadow-lg bg-white hover:shadow-xl transition duration-300 p-4 flex flex-col justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">{p.name}</h3>
                  <p className="text-gray-600 mt-1 whitespace-pre-line">{p.description}</p>
                  <div className="mt-3 text-sm text-gray-700 space-y-1">
                    <p><span className="font-medium">Prix:</span> {p.initialPrice} DT</p>
                    <p><span className="font-medium">Quantité:</span> {p.quantity}</p>
                    <p><span className="font-medium">TVA:</span> {p.tva}%</p>
                    {p.reference && <p><span className="font-medium">Référence:</span> {p.reference}</p>}
                    <p><span className="font-medium">Catégorie:</span> {categories.find(c => c._id === (typeof p.category === 'string' ? p.category : p.category?._id))?.name || '—'}</p>
                    <p><span className="font-medium">Sous-catégorie:</span> {subcategories.find(s => s._id === (typeof p.subcategory === 'string' ? p.subcategory : p.subcategory?._id))?.name || '—'}</p>
                    {p.addedBy && <p><span className="font-medium">Créé par:</span> {typeof p.addedBy === 'object' ? p.addedBy.email : p.addedBy}</p>}
                    {p.modifiedBy && <p><span className="font-medium">Modifié par:</span> {typeof p.modifiedBy === 'object' ? p.modifiedBy.email : p.modifiedBy}</p>}
                    {p.createdAt && <p><span className="font-medium">Créé le:</span> {new Date(p.createdAt).toLocaleDateString('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</p>}
                    {p.updatedAt && p.updatedAt !== p.createdAt && <p><span className="font-medium">Modifié le:</span> {new Date(p.updatedAt).toLocaleDateString('fr-FR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</p>}
                  </div>
                  {p.image && <div className="w-full h-40 mt-4 rounded-lg bg-white border flex items-center justify-center overflow-hidden"><img src={p.image} alt={p.name} className="max-h-full max-w-full object-contain" /></div>}
                </div>
                <div className="flex justify-between mt-4 text-sm">
                  <button onClick={() => handleEdit(p)} className="text-blue-600 hover:text-blue-800 font-medium">Modifier</button>
                  <button onClick={() => handleDelete(p._id)} className="text-red-600 hover:text-red-800 font-medium">Supprimer</button>
                </div>
              </div>
            ))}
          </div>
          {totalPages > 1 && (
            <div className="flex justify-center mt-6 space-x-1 items-center">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                className={`px-3 py-1 rounded border ${
                  currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'bg-white hover:bg-gray-200'
                }`}
              >
                &lt;
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => {
                  if (page === 1 || page === totalPages) return true;
                  if (page >= currentPage - 2 && page <= currentPage + 2) return true;
                  return false;
                })
                .map((page, i, arr) => {
                  const prev = arr[i - 1];
                  const isGap = prev && page - prev > 1;
                  return (
                    <React.Fragment key={page}>
                      {isGap && <span className="px-2 text-gray-500">...</span>}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded border ${
                          currentPage === page
                            ? 'bg-paragaia text-white'
                            : 'bg-white text-paragaia hover:bg-paragaiaLight'
                        }`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
                className={`px-3 py-1 rounded border ${
                  currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'bg-white hover:bg-gray-200'
                }`}
              >
                &gt;
              </button>
            </div>
          )}

        </div>
      </main>

{showModal && (
  <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4">
    <div className="bg-white w-full max-w-3xl max-h-[90vh] rounded-xl shadow-xl overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
      <h2 className="text-2xl font-semibold mb-6">{editMode ? 'Modifier' : 'Ajouter'} un produit</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex flex-col gap-4">
          {[
            { name: "name", type: "text", label: "Nom du produit", value: form.name },
            { name: "initialPrice", type: "number", label: "Prix", value: form.initialPrice },
            { name: "quantity", type: "number", label: "Quantité", value: form.quantity },
            { name: "tva", type: "number", label: "TVA (%)", value: form.tva },
            { name: "reference", type: "number", label: "Référence (optionnel)", value: form.reference },
          ].map(({ name, type, label, value }) => (
            <div key={name} className="relative w-full">
              <input
                type={type}
                name={name}
                value={value}
                onChange={handleInputChange}
                placeholder={label}
                className="peer w-full border rounded-md px-4 pt-5 pb-2 shadow-sm placeholder-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <label
                htmlFor={name}
                className="absolute left-4 top-2 text-sm text-gray-500 transition-all 
                  peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-base 
                  peer-placeholder-shown:text-gray-400 peer-focus:top-2 peer-focus:text-sm 
                  peer-focus:text-gray-500"
              >
                {label}
              </label>
              {name === 'reference' && (
                <p className="text-xs text-gray-500 mt-1">
                  Si vide, un numéro de référence unique sera généré automatiquement
                </p>
              )}
            </div>
          ))}

          <div className="relative w-full">
            <textarea
              name="description"
              value={form.description}
              onChange={handleInputChange}
              rows={3}
              placeholder="Description"
              className="peer w-full border rounded-md px-4 pt-5 pb-2 shadow-sm placeholder-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <label
              htmlFor="description"
              className="absolute left-4 top-2 text-sm text-gray-500 transition-all 
                peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-base 
                peer-placeholder-shown:text-gray-400 peer-focus:top-2 peer-focus:text-sm 
                peer-focus:text-gray-500"
            >
              Description
            </label>
          </div>

          <div
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
            className={`w-full border-2 ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-dashed border-blue-300'} rounded-md px-4 py-6 text-center cursor-pointer transition`}
          >
          <label className="text-blue-700 font-medium cursor-pointer">
            Glissez une image ici ou <span className="underline">cliquez pour choisir</span><br />
            <span className="text-sm text-gray-500">(Image carrée recommandée)</span>
            <input type="file" name="image" accept="image/*" onChange={handleInputChange} className="hidden" />
          </label>

          </div>
        </div>

        <div className="flex flex-col items-center gap-6">
          {editMode && products.find(p => p._id === editingId)?.image && (
            <div className="w-full">
              <label className="block text-sm font-medium mb-1">Image actuelle</label>
              <div className="w-full h-40 border bg-white flex items-center justify-center overflow-hidden rounded shadow">
                <img src={products.find(p => p._id === editingId)?.image} alt="Ancienne image" className="max-h-full max-w-full object-contain" />
              </div>
            </div>
          )}
          {form.image && (
            <div className="w-full">
              <label className="block text-sm font-medium mb-1">Aperçu nouvelle image</label>
              <div className="w-full h-40 border bg-white flex items-center justify-center overflow-hidden rounded shadow">
                <img src={URL.createObjectURL(form.image)} alt="Aperçu" className="max-h-full max-w-full object-contain" />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <div className="relative w-full">
          <label htmlFor="category" className="block text-sm text-gray-600 mb-1">Catégorie</label>
          <select
            name="category"
            id="category"
            value={form.category}
            onChange={handleInputChange}
            className={`w-full border rounded-md px-4 py-2 shadow-sm focus:outline-none focus:ring-2 ${
              multipleCategoriesOfSub.length > 1 && !form.category
                ? 'border-red-500 focus:ring-red-500'
                : 'focus:ring-blue-500'
            }`}
          >
            <option value="">Sélectionner une catégorie</option>
            {(multipleCategoriesOfSub.length > 0
              ? categories.filter(c => multipleCategoriesOfSub.includes(c._id))
              : categories
            ).map(c => (
              <option key={c._id} value={c._id}>{c.name}</option>
            ))}
          </select>

          {multipleCategoriesOfSub.length > 1 && !form.category && (
            <p className="text-sm text-orange-600 mt-1">
              Cette sous-catégorie est liée à plusieurs catégories. Veuillez en choisir une.
            </p>
          )}
        </div>

        <div className="relative w-full">
          <label htmlFor="subcategory" className="block text-sm text-gray-600 mb-1">Sous-catégorie</label>
          <select
            name="subcategory"
            id="subcategory"
            value={form.subcategory}
            onChange={handleInputChange}
            className="w-full border rounded-md px-4 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Sélectionner une sous-catégorie</option>
            {(form.category ? filteredSubs : subcategories).map(s => (
              <option key={s._id} value={s._id}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex justify-end gap-3 mt-8">
        <button onClick={() => setShowModal(false)} className="px-5 py-2 rounded-md border text-gray-600 hover:bg-gray-100">Annuler</button>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className={`px-6 py-2 rounded-md ${loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
        >
          {loading ? 'Veuillez patienter...' : (editMode ? 'Modifier' : 'Ajouter')}
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );


};

export default ProductsScreen;